/* @jsx jsx */

import { jsx } from '@emotion/react';
import { bindAll } from 'bind-event-listener';
import { useIntl } from 'react-intl-next';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { floatingContainerStyles, columnLayout, promptPaddingStyles, footerTextStyles, footerSectionStyles, footerSectionSpacerStyles } from './styles';
import messages from './messages';
import { RefinementTag } from '../../components/RefinementTag/RefinementTag';
import { useEffect, useRef, Fragment, useMemo, useState, useCallback } from 'react';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { scrollMarginStyles } from '../../components/FloatingContainer/styles';
import { Suggestions } from '../../components/Suggestions/Suggestions';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { AtlassianIntelligenceLogo } from '../../components/AtlassianIntelligenceLogo/AtlassianIntelligenceLogo';
import { getNextSuggestion } from './getNextSuggestion';
import { CancelEscButton } from '../../components/CancelEscButton/CancelEscButton';
import { useContainerBreakpoints } from '../../hooks/use-container-breakpoints';
/**
 * This screen is used when the user is interacting with the AI command palette.
 *
 * ---
 *
 * If there is no preset prompt label, show the suggestions.
 * Otherwise, show the preset prompt label and no suggestions.
 */
export const UserInputCommandPalette = ({
  inputValue,
  inputADFValue,
  onInputUpdate,
  onADFUpdate,
  presetPromptLabel,
  onSubmit,
  onCancel,
  onEscapeKeyPressed,
  onRefinementTagRemove,
  placeholder,
  getSuggestions,
  suggestionsHeading,
  focusInputRef,
  PromptEditor
}) => {
  const {
    ref,
    breakpoint
  } = useContainerBreakpoints();
  const promptWithSuggestionsRef = useRef(null);
  const {
    formatMessage
  } = useIntl();

  /**
   * Scroll prompt into view as suggestions are not always fully visible
   * when PromptInput is autofocused.
   */
  useEffect(() => {
    // Add event listener to handle escape key press
    const {
      current: element
    } = promptWithSuggestionsRef;

    // If the element is not mounted, do nothing
    if (!element) {
      return;
    }

    // setTimeout is for Safari so that it scrolls even with the
    // PromptInput autoFocus scroll behaviour.
    setTimeout(() => {
      if (promptWithSuggestionsRef.current) {
        promptWithSuggestionsRef.current.scrollIntoView({
          block: 'nearest'
        });
      }
    }, 0);

    /**
     * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
     * other escape key handlers will not override this behaviour.
     * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
     */
    const handleEscapeKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onEscapeKeyPressed();
      }
    };

    /**
     * Handle KeyUp event to prevent unexpected escape behaviours when
     * there are other escape handlers outside of component (e.g. in product)
     */
    const handleEscapeKeyUp = event => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    return bindAll(element, [{
      type: 'keydown',
      listener: handleEscapeKeyDown
    }, {
      type: 'keyup',
      listener: handleEscapeKeyUp
    }]);
  }, [promptWithSuggestionsRef, onEscapeKeyPressed]);

  /*
   * If there is no refinement tag, show the suggestions.
   * Otherwise, show the refinement tag and no suggestions.
   */
  let suggestions = useMemo(() => {
    return presetPromptLabel === undefined ? getSuggestions(inputValue) : [];
  }, [presetPromptLabel, getSuggestions, inputValue]);
  let tag = presetPromptLabel === undefined ? undefined : jsx(RefinementTag, {
    label: presetPromptLabel,
    onClick: onRefinementTagRemove
  });
  const suggestionValues = useMemo(() => {
    return (suggestions || []).map(suggestion => suggestion.value);
  }, [suggestions]);
  const [currentSuggestion, _setCurrentSuggestion] = useState(suggestionValues.length > 0 ? suggestionValues[0] : '');

  /**
   * Handles the behaviour for when a user changes the suggestion.
   *
   * At the moment this is used to highlight the entire editor document for
   * relevant suggestions.
   */
  const handleSuggestionChanged = useCallback(suggestion => {
    const suggestionObject = suggestions.find(s => s.value === suggestion);
    suggestionObject === null || suggestionObject === void 0 ? void 0 : suggestionObject.onSuggestionFocused();
  }, [suggestions]);
  const setCurrentSuggestion = useCallback(newValue => {
    handleSuggestionChanged(newValue);
    _setCurrentSuggestion(newValue);
  }, [_setCurrentSuggestion, handleSuggestionChanged]);

  // Select first option by default
  useEffect(() => {
    if (suggestionValues.includes(currentSuggestion) || suggestionValues.length === 0) {
      return;
    }
    setCurrentSuggestion(suggestionValues[0]);
  }, [currentSuggestion, suggestionValues, setCurrentSuggestion]);
  const onArrowKeyDown = useCallback(event => {
    // Ignore if any key modifiers are held
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return;
    }

    // If theres one or less suggestions, no need to navigate and allow normal
    // multiline text traversal
    if (suggestionValues.length <= 1) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        {
          const nextSuggestion = getNextSuggestion(suggestionValues, currentSuggestion, event.key);
          setCurrentSuggestion(nextSuggestion);
          event.preventDefault();
          event.stopPropagation();
          break;
        }
      default:
        {
          return;
        }
    }
  }, [currentSuggestion, setCurrentSuggestion, suggestionValues]);
  const setSuggestion = useCallback(suggestion => {
    setCurrentSuggestion(suggestion.value);
  }, [setCurrentSuggestion]);
  const suggestionDiv = jsx(Suggestions, {
    suggestions: suggestions,
    suggestionsHeading: suggestionsHeading,
    value: currentSuggestion,
    setSuggestion: setSuggestion
  });
  const onFormSubmit = useCallback(() => {
    const isRefinementPrompt = !!presetPromptLabel;
    if (currentSuggestion === '' || isRefinementPrompt) {
      onSubmit();
      return;
    }
    const suggestion = (suggestions || []).find(suggestion => suggestion.value === currentSuggestion);
    if (suggestion) {
      suggestion.onSelect();
    }
    setCurrentSuggestion('');
  }, [suggestions, currentSuggestion, setCurrentSuggestion, onSubmit, presetPromptLabel]);

  // if a refinement tag like brainstorm was selected, we continue showing these legacy buttons
  const showLegacySubmitCancelButtons = Boolean(presetPromptLabel);
  const promptForm = jsx(PromptForm, {
    showButtons: showLegacySubmitCancelButtons,
    placeholder: placeholder,
    value: inputValue,
    adfValue: inputADFValue,
    onFormSubmit: onFormSubmit,
    onCancel: onCancel,
    onInputChange: onInputUpdate,
    onADFChange: onADFUpdate,
    tag: tag,
    focusInputRef: focusInputRef,
    showLogo: false,
    onInputKeyDown: onArrowKeyDown,
    PromptEditor: PromptEditor
  });
  return (
    // Need div for css composition
    // https://atlassian.design/components/eslint-plugin-design-system/usage#prefer-primitives
    // eslint-disable-next-line
    jsx("div", {
      ref: promptWithSuggestionsRef
      // TODO: assert this remains stable
      ,
      "data-testid": "user-input-command-palette-screen",
      className: `user-input-command-palette-screen ${AI_MODAL_SCREEN_CLASS}`,
      css: [columnLayout, scrollMarginStyles]
    }, jsx(VisuallyHidden, null, formatMessage(messages.commandPaletteAriaName)), jsx(FloatingContainer, {
      ref: ref,
      css: floatingContainerStyles,
      "data-testid": "prompt-form-screen"
    }, jsx("div", {
      css: promptPaddingStyles
    }, promptForm), !tag && (suggestions || inputValue) && suggestionDiv, jsx("div", {
      css: footerSectionStyles
    }, jsx(AtlassianIntelligenceLogo, null), jsx("div", {
      "data-testid": "footer-brand-text",
      css: footerTextStyles(breakpoint)
    }, formatMessage(messages.footerText)), !showLegacySubmitCancelButtons && jsx(Fragment, null, jsx("div", {
      css: footerSectionSpacerStyles
    }), jsx("div", null, jsx(CancelEscButton, {
      onCancel: onCancel
    }))))))
  );
};